import moment from 'moment';

import {
  CRISIS_PLAN_FUNCTIONAL_BEHAVIOR_OPTIONS,
  LEGACY_CRISIS_PLAN_FUNCTIONAL_BEHAVIOR,
} from './constants';
import { CRISIS_PLAN_FORM_SCHEMA } from './validationSchema';
import { API_DATE_FORMAT, MONTH_YEAR_DISPLAY_FORMAT } from '../../../../../constants';

import type {
  ITreatmentPlanV3AssessmentBehavior,
  ITreatmentPlanV3CrisisPage,
} from '../../../../../store/treatmentPlanV3';
import type {
  CrisisPlanFieldName,
  CrisisPlanFormValues,
  CrisisPlanPayload,
  ITreatmentPlanV3CrisisPlan,
} from '../../../../common';

export function getCrisisPlanRelatedBehaviorLabel(
  assessmentBehaviorId: number | null,
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
): string {
  if (assessmentBehaviorId == null) return '';
  return behaviors.find((row) => row.id === assessmentBehaviorId)?.behavior ?? '';
}

export function getSelectableRelatedBehaviors(
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
) {
  return behaviors.filter((row) => row.behavior);
}

export function getCrisisPlanRelatedBehaviorOptions(
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
  crisisPlans: ReadonlyArray<ITreatmentPlanV3CrisisPlan>,
  currentPlanId?: number,
) {
  const takenIds = new Set(
    crisisPlans
      .filter((plan) => plan.id !== currentPlanId && plan.assessment_behavior_id != null)
      .map((plan) => plan.assessment_behavior_id),
  );
  return getSelectableRelatedBehaviors(behaviors)
    .filter((row) => !takenIds.has(row.id))
    .map((row) => ({ value: String(row.id), label: row.behavior }));
}

export function areAllRelatedBehaviorsSelected(
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
  crisisPlans: ReadonlyArray<ITreatmentPlanV3CrisisPlan>,
) {
  const selectableIds = new Set(getSelectableRelatedBehaviors(behaviors).map((row) => row.id));
  return (
    selectableIds.size > 0 &&
    new Set(
      crisisPlans
        .map((plan) => plan.assessment_behavior_id)
        .filter((id) => id != null && selectableIds.has(id)),
    ).size >= selectableIds.size
  );
}

function isLegacyFunctionalBehavior(
  value: string,
): value is keyof typeof LEGACY_CRISIS_PLAN_FUNCTIONAL_BEHAVIOR {
  return Object.hasOwn(LEGACY_CRISIS_PLAN_FUNCTIONAL_BEHAVIOR, value);
}

function mapFunctionalBehaviorToFormValue(values: string[] | null): string {
  if (!values?.length) return '';
  const mapped = values.map((value) =>
    isLegacyFunctionalBehavior(value) ? LEGACY_CRISIS_PLAN_FUNCTIONAL_BEHAVIOR[value] : value,
  );
  return (
    mapped.find((value) =>
      CRISIS_PLAN_FUNCTIONAL_BEHAVIOR_OPTIONS.some((option) => option.value === value),
    ) ?? ''
  );
}

export function toCrisisPlanPayload(values: CrisisPlanFormValues): CrisisPlanPayload {
  return {
    assessment_behavior_id: values.assessment_behavior_id
      ? Number(values.assessment_behavior_id)
      : null,
    crisis_creation_date:
      values.crisis_creation_date?.clone().startOf('month').format(API_DATE_FORMAT) ?? null,
    operational_definition: values.operational_definition,
    antecedent_interventions: values.antecedent_interventions,
    precursors_action_steps: values.precursors_action_steps,
    escalation_intervention_plan: values.escalation_intervention_plan,
    follow_up_steps: values.follow_up_steps,
    functional_behavior: values.functional_behavior ? [values.functional_behavior] : [],
    consequence_strategies: values.consequence_strategies,
  };
}

export function toCrisisPlanFieldPayload<K extends CrisisPlanFieldName>(
  name: K,
  values: CrisisPlanFormValues,
): CrisisPlanPayload {
  // Send ONLY the changed field: bundling others lets an older in-flight save clobber newer edits.
  const data: CrisisPlanPayload = {};
  data[name] = toCrisisPlanPayload(values)[name];
  return data;
}

export function mapCrisisPlanRowToValues(row: ITreatmentPlanV3CrisisPlan): CrisisPlanFormValues {
  return {
    assessment_behavior_id:
      row.assessment_behavior_id == null ? '' : String(row.assessment_behavior_id),
    crisis_creation_date: row.crisis_creation_date
      ? moment(row.crisis_creation_date).startOf('month')
      : null,
    operational_definition: row.operational_definition ?? '',
    antecedent_interventions: row.antecedent_interventions ?? '',
    precursors_action_steps: row.precursors_action_steps ?? '',
    escalation_intervention_plan: row.escalation_intervention_plan ?? '',
    follow_up_steps: row.follow_up_steps ?? '',
    functional_behavior: mapFunctionalBehaviorToFormValue(row.functional_behavior),
    consequence_strategies: row.consequence_strategies ?? '',
  };
}

export function getCrisisPlanRowLabel(
  row: ITreatmentPlanV3CrisisPlan,
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
): string {
  return [
    getCrisisPlanRelatedBehaviorLabel(row.assessment_behavior_id, behaviors),
    row.crisis_creation_date
      ? moment(row.crisis_creation_date).format(MONTH_YEAR_DISPLAY_FORMAT)
      : null,
  ]
    .filter(Boolean)
    .join(' - ');
}

export function getCrisisPlanDeleteMessage(
  row: ITreatmentPlanV3CrisisPlan,
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
): string {
  const behaviorLabel = getCrisisPlanRelatedBehaviorLabel(row.assessment_behavior_id, behaviors);
  if (!behaviorLabel) {
    return 'Are you sure you want to delete this crisis plan?';
  }
  return `Are you sure you want to delete the crisis plan for "${behaviorLabel}"?`;
}

export function hasCrisisPlanFormErrors(
  page: ITreatmentPlanV3CrisisPage,
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
) {
  if (page.no_crisis_plan_needed) return false;
  if (page.crisis_plans.length === 0) return true;
  if (
    page.crisis_plans.some(
      (row) => !CRISIS_PLAN_FORM_SCHEMA.isValidSync(mapCrisisPlanRowToValues(row)),
    )
  ) {
    return true;
  }
  if (getSelectableRelatedBehaviors(behaviors).length === 0) return false;
  return page.crisis_plans.some((row) => row.assessment_behavior_id == null);
}
